import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {
    DocumentFormScope,
    DocumentScope,
    DocumentUpdateDto,
    Form,
    FormScope,
    FormsService,
} from '@core/interfaces/common/forms';
import {FormsApi} from '@core/backend/common/api/forms.api';
import {
    APIResponse,
    Filter,
    FilterFieldOption,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    PagingResponse,
    PresignedURL,
} from '@core/interfaces/system/system-common';
import {FormResponseDto} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {DownloadCSVUrl} from '@core/interfaces/engin/workflow-validation';
import {Document, DocumentCreate} from '@core/interfaces/common/document';
import {Dataset} from '@core/interfaces/common/dataset';

@Injectable()
export class FormsServiceImpl extends FormsService {
    constructor(private api: FormsApi) {
        super();
    }

    // Form content: basic content
    public createForm(name: string, description: string): Observable<APIResponse<Form>> {
        return this.api.createForm(name, description);
    }

    public toggleFormPublish(formId: number, isPublished: boolean): Observable<APIResponse<Form>> {
        return this.api.toggleFormPublish(formId, isPublished);
    }

    public updateForm(formId: number, name: string, description: string): Observable<APIResponse<Form>> {
        return this.api.updateForm(formId, name, description);
    }

    public getInspectionFormDto(formId: number): Observable<APIResponse<FormResponseDto>> {
        return this.api.getInspectionFormDto(formId);
    }

    // Form content: scope
    public getFormScope(formId: number): Observable<APIResponse<Filter[]>> {
        return this.api.getFormScope(formId);
    }

    public setFormScope(formId: number, filters: Filter[]): Observable<APIResponse<any>> {
        return this.api.setFormScope(formId, filters);
    }

    public getFilterOptions(fieldKey: string, searchString: string): Observable<APIResponse<FilterFieldOption[]>> {
        return this.api.getFilterOptions(fieldKey, searchString);
    }

    // Form content: config
    public commitFormConfig(
        formId: number,
        isConfirmed: boolean,
        dataPrepToolId: number,
    ): Observable<APIResponse<Form>> {
        return this.api.commitFormConfig(formId, isConfirmed, dataPrepToolId);
    }

    public downloadFormConfig(formId: number): Observable<APIResponse<DownloadCSVUrl[]>> {
        return this.api.downloadFormConfig(formId);
    }

    // Other
    getInspectionFormsDataSource(): Observable<DataSource> {
        return this.api.getInspectionFormsDataSource();
    }
    getSelectedInspectionFormsDataSource(selectedId: number[]): Observable<PagingResponse<FormScope>> {
        return this.api.getSelectedInspectionFormsDataSource(selectedId);
    }
    getInspectionForms(pageSize?: number): Observable<Form[]> {
        return this.api.getInspectionForms(pageSize);
    }

    public duplicateForm(formId: number): Observable<APIResponse<any>> {
        return this.api.duplicateForm(formId);
    }

    public deleteForm(formId: number): Observable<APIResponse<any>> {
        return this.api.deleteForm(formId);
    }

    public downloadInspectionForms(): Observable<APIResponse<DownloadCSVUrl>> {
        return this.api.downloadInspectionForms();
    }
    updateDocumentScopeByFormId(
        formIds: number,
        params: {inspectionDocumentIds: number[]},
    ): Observable<APIResponse<Document>> {
        return this.api.updateDocumentScopeByFormId(formIds, params);
    }
    // Document management

    listFiles(): Observable<DataSource> {
        return this.api.listFiles();
    }
    listFilesByFormId(formId: number): Observable<DataSource> {
        return this.api.listFilesByFormId(formId);
    }
    listSelectedFiles(selectedId: number[]): Observable<PagingResponse<DocumentScope>> {
        return this.api.listSelectedFiles(selectedId);
    }
    create(document: DocumentCreate): Observable<APIResponse<Document>> {
        return this.api.create(document);
    }
    update(documentId: number, document: DocumentUpdateDto): Observable<APIResponse<Document>> {
        return this.api.update(documentId, document);
    }

    public deleteDocument(formId: number): Observable<APIResponse<boolean>> {
        return this.api.deleteDocument(formId);
    }

    updateDocumentScope(documentId: number, params: {formIds: number[]}): Observable<APIResponse<Document>> {
        return this.api.updateDocumentScope(documentId, params);
    }

    getSelectedFormsByDocumentId(documentId: number): Observable<APIResponse<PagingResponse<DocumentFormScope>>> {
        return this.api.getSelectedFormsByDocumentId(documentId);
    }
    getSelectedDocumentByFormsId(formId: number): Observable<APIResponse<PagingResponse<DocumentFormScope>>> {
        return this.api.getSelectedDocumentByFormsId(formId);
    }

    getPresignedUrl(fileName: string, fileFormat: string): Observable<APIResponse<PresignedURL>> {
        return this.api.getPresignedUrl(fileName, fileFormat);
    }

    uploadToPresignedUrl(url: string, fileData: File): Observable<any> {
        return this.api.upload(`${url}`, fileData);
    }
    getDownloadUrl(documentId: number): Observable<string> {
        return this.api.getDownloadUrl(documentId);
    }
    getFilterFieldOptionsTable(
        fieldKey: string,
        searchString: string,
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        const req: FilterFieldOptionRequest = {
            fieldKey: fieldKey,
            searchString: searchString,
        };
        return this.getFilterFieldOptions(req);
    }
    getFilterFieldOptions(req: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.getFilterFieldOptions(req);
    }
}
